import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Clock, Car, Accessibility, Beef, Cake, UtensilsCrossed, WineOff, AlertTriangle } from 'lucide-react';
import Reveal from './Reveal';
import OpenStatusBadge from './OpenStatusBadge';

const LocationInfo: React.FC = () => {
  const { t } = useTranslation();
  const todayIndex = (() => {
    const jsDay = new Date().getDay();
    return jsDay === 0 ? 6 : jsDay - 1;
  })();

  const schedule = [
    { day: t('hours.monday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.tuesday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.wednesday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.thursday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.friday'), lunch: t('hours.lunch'), dinner: t('hours.dinnerFriday') },
    { day: t('hours.saturday'), lunch: t('hours.lunchWeekend'), dinner: t('hours.dinnerSaturday') },
    { day: t('hours.sunday'), lunch: t('hours.lunchWeekend'), dinner: t('hours.dinner') }
  ];

  return (
    <>
      {/* Location Info */}
      <section className="py-8 md:py-12 lg:py-16 bg-white relative overflow-hidden">
        <img
          src="/restaurant_photos/gold_fan.png"
          alt=""
          className="absolute right-0 top-1/2 -translate-y-1/2 w-64 md:w-72 lg:w-80 opacity-20 pointer-events-none z-0 rotate-180"
        />

        <div className="section-padding relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Reveal direction="left">
              <div className="h-[500px] rounded-lg overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2628.4456337899486!2d2.1681019!3d48.7846875!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e67d6f0c8c5b63%3A0x3e8e8e8e8e8e8e8e!2s1%20Rue%20Andr%C3%A9%20Citro%C3%ABn%2C%2078140%20V%C3%A9lizy-Villacoublay%2C%20France!5e0!3m2!1sfr!2sfr!4v1703268900000!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pacific 78 Restaurant Location"
                />
              </div>
            </Reveal>

            <Reveal direction="right">
              <h2 className="text-3xl font-elegant font-bold mb-8 text-japanese-black">
                Informations Pratiques
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="text-japanese-red mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-lg">{t('location.address')}</p>
                    <p className="text-japanese-gray">{t('location.inMall')}</p>
                    <p className="text-japanese-gray">{t('location.aboveCastorama')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Car className="text-japanese-red mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-lg">Parking</p>
                    <p className="text-japanese-gray">{t('info.parking')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Accessibility className="text-japanese-red mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-lg">Accessibilité</p>
                    <p className="text-japanese-gray">{t('info.pmr')}</p>
                  </div>
                </div>
              </div>

              <a
                href="https://maps.app.goo.gl/8Hy52cCZLU2brFfj6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-8 bg-red-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-red-700 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-luxury-gold/30 transform hover:-translate-y-1 border-2 border-red-700 hover:border-luxury-gold"
              >
                {t('location.viewMap')}
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Opening Hours */}
      <section className="py-8 md:py-12 lg:py-16 bg-japanese-cream">
        <div className="section-padding">
          <Reveal className="text-center mb-6 md:mb-8 lg:mb-12">
            <h2 className="text-4xl font-elegant font-bold mb-4 text-japanese-black">
              {t('home.hoursAndInfoTitle')}
            </h2>
            <div className="flex justify-center mb-4">
              <OpenStatusBadge />
            </div>
            <div className="w-24 h-1 bg-japanese-red mx-auto" />
          </Reveal>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="grid grid-cols-3 bg-japanese-red text-white font-semibold">
                <div className="p-4 text-center">Jour</div>
                <div className="p-4 text-center border-l border-white/20">Déjeuner</div>
                <div className="p-4 text-center border-l border-white/20">Dîner</div>
              </div>
              {schedule.map((item, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-3 transition-colors duration-300 ${
                    index === todayIndex
                      ? 'bg-luxury-gold/15 font-semibold'
                      : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <div className="p-4 font-body text-lg text-luxury-black flex items-center gap-2">
                    {item.day}
                    {index === todayIndex && (
                      <span className="w-1.5 h-1.5 rounded-full bg-luxury-gold" />
                    )}
                  </div>
                  <div className="p-4 text-center text-lg text-luxury-black">{item.lunch}</div>
                  <div className="p-4 text-center text-lg text-luxury-black">{item.dinner}</div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <div className="flex items-center justify-center gap-2 text-lg">
                <Clock className="text-japanese-red" size={20} />
                <span className="font-semibold">{t('info.open7days')}</span>
              </div>
              <p className="text-japanese-gray mt-2">{t('info.noReservation')}</p>
            </div>

            {/* Practical info recap */}
            <div className="flex flex-wrap justify-center gap-6 mt-8 pt-8 border-t border-luxury-gold/10">
              <div className="flex items-center gap-2 text-japanese-gray">
                <Beef size={18} className="text-luxury-gold" />
                <span className="text-sm">{t('info.halal')}</span>
              </div>
              <div className="flex items-center gap-2 text-japanese-gray">
                <UtensilsCrossed size={18} className="text-luxury-gold" />
                <span className="text-sm">{t('info.vegetarian')}</span>
              </div>
              <div className="flex items-center gap-2 text-japanese-gray">
                <Cake size={18} className="text-luxury-gold" />
                <span className="text-sm">{t('info.birthday')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accepted Payments + Policies */}
      <section className="py-8 md:py-12 lg:py-16 bg-white relative">
        <div className="section-padding relative z-10 max-w-3xl mx-auto">
          <Reveal className="text-center mb-10">
            <h3 className="text-2xl font-semibold mb-6 text-luxury-gold">
              Moyens de paiement acceptés
            </h3>
            <p className="text-lg text-japanese-gray">
              {t('info.payment')}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Reveal delay={0.1}>
              <div className="border border-luxury-gold/30 rounded-lg p-5 h-full text-center shadow-sm">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <WineOff className="text-luxury-gold flex-shrink-0" size={20} />
                  <h4 className="text-base font-display text-luxury-black">
                    {t('outsideDrinks.title')}
                  </h4>
                </div>
                <p className="text-luxury-gray-dark font-body text-sm">
                  {t('outsideDrinks.fee')}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="border border-luxury-gold/30 rounded-lg p-5 h-full text-center shadow-sm">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <AlertTriangle className="text-luxury-gold flex-shrink-0" size={20} />
                  <h4 className="text-base font-display text-luxury-black">
                    {t('home.wasteTitle')}
                  </h4>
                </div>
                <p className="text-luxury-gray-dark font-body text-sm">
                  {t('prices.wasteWarning')} {t('prices.noTakeaway')}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default LocationInfo;
